// Eskom PARCC — impersonation preview banner.
//
// A persistent, unmissable bar shown while an admin is previewing as another role.
// It names the previewed role/division/person, states the preview is read-only,
// and offers a one-click exit. Amber + dark text reads on either theme.
import { EyeOff } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { ROLE_LABELS } from '../../lib/permissions';

export function ImpersonationBar(): React.JSX.Element | null {
  const { isImpersonating, grant, stopImpersonating } = useAuth();
  if (!isImpersonating || !grant) return null;

  const roleLabel = ROLE_LABELS[grant.role] ?? grant.role;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        // Sticky above the sticky page headers: a "read-only preview" warning
        // that scrolls out of reach (with its only Exit button) defeats itself.
        position: 'sticky',
        top: 0,
        zIndex: 60,
        background: 'var(--warn-bg, #f59e0b)',
        color: 'var(--warn-fg, #1a1300)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        fontWeight: 600,
        fontSize: 14,
        flexWrap: 'wrap',
      }}
    >
      <span>
        Previewing as{' '}
        <strong>
          {roleLabel}
          {grant.division ? ` · ${grant.division}` : ''}
        </strong>{' '}
        ({grant.email}) — read-only.
      </span>
      <button
        type="button"
        onClick={stopImpersonating}
        style={{
          background: '#1a1300',
          color: '#f5b942',
          border: 'none',
          borderRadius: 6,
          padding: '4px 12px',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <EyeOff size={14} aria-hidden /> Exit preview
      </button>
    </div>
  );
}
